import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Sparkles, Zap, CheckCircle2, ArrowRight } from 'lucide-react';
import { useBillingStore, TIER_META, TIER_LIMITS, type Tier } from '@/stores/billingStore';
import { cn } from '@/lib/cn';

/**
 * Global upgrade-prompt modal. Any caller can nudge the user to upgrade
 * via billingStore.openUpgrade('feature name'); this component listens
 * for upgradeModalFeature and renders.
 */
export function UpgradeModal() {
  const open     = useBillingStore((s) => s.upgradeModalFeature);
  const close    = useBillingStore((s) => s.closeUpgrade);
  const current  = useBillingStore((s) => s.tier);
  const setTier  = useBillingStore((s) => s.setTier);

  const recommended: Tier = tierThatUnlocks(current);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
            onClick={close}
          />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          >
            <div className="w-full max-w-lg bg-bg-raised rounded-3xl border border-border shadow-floating overflow-hidden">
              <div className="relative p-8 text-center text-white bg-gradient-myday bg-[length:200%_200%] animate-gradient-pan">
                <button onClick={close} className="absolute top-3 right-3 p-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10">
                  <X className="h-4 w-4" />
                </button>
                <div className="inline-flex h-12 w-12 rounded-2xl bg-white/20 backdrop-blur items-center justify-center mb-3 shadow-glow">
                  <Sparkles className="h-6 w-6 drop-shadow" />
                </div>
                <h2 className="font-display text-2xl font-bold">Unlock {open}</h2>
                <p className="text-sm text-white/90 mt-1">
                  You're on <span className="font-semibold">{TIER_META[current].name}</span>.
                  Upgrade to <span className="font-semibold">{TIER_META[recommended].name}</span> to get this feature.
                </p>
              </div>

              <div className="p-6">
                <PlanSummary tier={recommended} />
                <div className="mt-5 flex items-center gap-2">
                  <button
                    onClick={() => { setTier(recommended); close(); }}
                    className="btn-primary flex-1"
                  >
                    <Zap className="h-4 w-4" /> Switch to {TIER_META[recommended].name}
                  </button>
                  <Link to="/billing" onClick={close} className="btn-secondary">Compare plans</Link>
                </div>
                <p className="text-[11px] text-fg-muted text-center mt-3">
                  Plan switches apply instantly in preview mode. Payment kicks in when Polar is wired up.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function tierThatUnlocks(current: Tier): Tier {
  const order: Tier[] = ['free', 'basic', 'standard', 'pro', 'enterprise'];
  const i = order.indexOf(current);
  // Jump two tiers if you're free (usually free→standard lands with the most pop),
  // else just one.
  return order[Math.min(order.length - 1, i + (current === 'free' ? 2 : 1))];
}

function PlanSummary({ tier }: { tier: Tier }) {
  const meta = TIER_META[tier];
  const limits = TIER_LIMITS[tier];

  const points: string[] = [];
  if (limits.maxBoards === -1) points.push('Unlimited boards & items');
  if (limits.maxAutomations === -1 || limits.maxAutomations > 0) {
    points.push(limits.maxAutomations === -1 ? 'Unlimited automations' : `${limits.maxAutomations.toLocaleString()} automation runs / month`);
  }
  if (limits.dashboards) points.push('Dashboards');
  if (limits.integrations) points.push('All integrations');
  if (limits.chartView) points.push('Chart view');
  if (limits.formulaColumn) points.push('Formula + mirror columns');
  if (limits.timeTracking) points.push('Time tracking');
  if (limits.aiRequestsPerDay === -1) points.push('Unlimited AI');
  else points.push(`${limits.aiRequestsPerDay} AI requests / day`);
  if (limits.privateBoards) points.push('Private boards');
  if (limits.sso) points.push('SSO / SAML');
  if (limits.auditLog) points.push('Audit log');

  const price = meta.price === 'custom' ? 'Custom' : `$${meta.price.monthly}`;

  return (
    <div className={cn('rounded-2xl border border-accent/40 bg-accent-soft/30 p-5')}>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-display text-2xl font-bold">{meta.name}</span>
        <span className="text-lg font-bold">{price}</span>
        {meta.price !== 'custom' && <span className="text-xs text-fg-muted">/ user / mo</span>}
      </div>
      <ul className="space-y-1.5">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> {p}
          </li>
        ))}
      </ul>
      <Link to="/pricing" className="mt-3 text-xs text-accent font-semibold inline-flex items-center gap-1 hover:underline">
        See all tiers <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
